import type { Catalog, SKU, Floor } from '@gorampe/catalog-adapter';
import type { SceneLayout } from '../types';
import type { BOMNode, BOMGroup, BOMLeaf } from './types';
import {
  selectPatteSku,
  selectTransitionSku,
  selectPlatformSku,
  selectTreadSku,
  selectCornerPostSku,
} from './skuMapping';

/**
 * Enrich a BOMLeaf with catalog metadata (weight, material, image, revision, price).
 * Returns the leaf unchanged when catalog is undefined or when the partNumber is
 * not found in the catalog (e.g. 3D-only synthetic refs like "frame-tube-X").
 */
function enrichLeaf(leaf: BOMLeaf, catalog?: Catalog): BOMLeaf {
  if (!catalog) return leaf;
  const product = catalog.products.get(leaf.partNumber as SKU);
  if (!product) return leaf;

  const enriched: BOMLeaf = { ...leaf };
  if (product.weightLbs != null) enriched.weightLbs = product.weightLbs;
  // Only override material if the leaf doesn't already carry an explicit one
  // (some leaves already set 'Aluminum 6005A-T61' as a hand-curated fallback).
  if (product.material) enriched.material = product.material;
  if (product.imageUrl) enriched.imageUrl = product.imageUrl;
  if (product.drawingDate) enriched.drawingDate = product.drawingDate;
  if (product.revision != null) enriched.revision = product.revision;
  if (product.pricing?.sellCad != null && product.pricing.sellCad > 0) {
    enriched.priceCadUnit = product.pricing.sellCad;
    enriched.priceCadTotal = product.pricing.sellCad * (leaf.qty ?? 1);
  }
  return enriched;
}

export function aggregateBOM(layout: SceneLayout, catalog?: Catalog): BOMNode[] {
  const floor: Floor = layout.floor ?? 'plein';

  const plateformes: BOMLeaf[] = layout.platforms.map((p) => ({
    kind: 'leaf',
    refId: p.id,
    // Bug 3 fix: use selectPlatformSku so turn-landing maps to the SKU that
    // actually matches the 3D geometry (TURN_LANDING_SIZE_IN=48 → F01005),
    // and so floor=mailles selects the MAILLES variant (F01001).
    partNumber: selectPlatformSku(p.widthIn, p.depthIn, floor, p.kind),
    label: `${p.kind === 'head' ? 'Plateforme tête' : 'Palier'} ${p.widthIn}"x${p.depthIn}"`,
    qty: 1,
    dimensions: { widthIn: p.widthIn, depthIn: p.depthIn },
  }));

  const trottoirs: BOMLeaf[] = layout.treads.map((t) => ({
    kind: 'leaf',
    refId: t.id,
    // Bug 2 fix: selectTreadSku considers BOTH widthIn AND lengthIn.
    partNumber: selectTreadSku(t.widthIn, t.lengthIn, floor),
    label: `Trottoir ${t.lengthIn}"x${t.widthIn}"`,
    qty: 1,
    dimensions: { lengthIn: t.lengthIn, widthIn: t.widthIn },
    cutOnSite: false,
    material: 'Aluminum 6005A-T61',
  }));

  const railsLeaves: BOMLeaf[] = layout.rails.map((r) => ({
    kind: 'leaf',
    refId: r.id,
    partNumber: r.type === 'ajoure' ? 'F03001' : 'F03039',
    label: `Garde-corps ${r.type} ${r.lengthIn}" (${r.side})`,
    qty: 1,
    dimensions: { lengthIn: r.lengthIn, heightIn: r.heightIn },
  }));

  const visserie: BOMLeaf[] = layout.hardwareGroups.map((g) => ({
    kind: 'leaf',
    refId: g.partNumber,
    partNumber: g.partNumber,
    label: prettyHardwareLabel(g.geometry),
    qty: g.placements.length,
  }));

  const restLandings: BOMLeaf[] = layout.landings.map((l) => ({
    kind: 'leaf',
    refId: l.id,
    partNumber: selectPlatformSku(l.widthIn, l.depthIn, floor, 'rest-landing'),
    label: `Palier de repos ${l.widthIn}"x${l.depthIn}"`,
    qty: 1,
    dimensions: { widthIn: l.widthIn, depthIn: l.depthIn },
  }));

  const pattes: BOMLeaf[] = layout.pattes.map((p) => ({
    kind: 'leaf',
    refId: p.id,
    // Bug 1 fix: pattes must map to the F04xxx that matches the actual
    // height (F04008-F04019), not the synthetic placeholder F04003 (which
    // is an X-bracing, not a leg).
    partNumber: p.partNumber ?? selectPatteSku(p.heightIn),
    label: `Patte ${p.heightIn.toFixed(0)}" hauteur`,
    qty: 1,
    dimensions: { heightIn: p.heightIn, widthIn: p.widthIn },
  }));

  const transitions: BOMLeaf[] = layout.transitions.map((t) => ({
    kind: 'leaf',
    refId: t.id,
    partNumber: t.partNumber ?? selectTransitionSku(t.widthIn, floor),
    label: `Transition de sol ${t.widthIn}"`,
    qty: 1,
    dimensions: { widthIn: t.widthIn },
  }));

  const cornerPosts: BOMLeaf[] = layout.cornerPosts.map((c) => ({
    kind: 'leaf',
    refId: c.id,
    partNumber: c.partNumber ?? selectCornerPostSku(c.heightIn),
    label: `Poteau d'angle ${c.heightIn}"`,
    qty: 1,
    dimensions: { heightIn: c.heightIn },
  }));

  const allGroups: BOMGroup[] = [
    {
      kind: 'group',
      label: 'Plateformes',
      children: [...plateformes, ...restLandings].map((l) => enrichLeaf(l, catalog)),
    },
    { kind: 'group', label: 'Trottoirs', children: trottoirs.map((l) => enrichLeaf(l, catalog)) },
    {
      kind: 'group',
      label: 'Garde-corps',
      children: railsLeaves.map((l) => enrichLeaf(l, catalog)),
    },
    {
      kind: 'group',
      label: 'Pattes (supports)',
      children: pattes.map((l) => enrichLeaf(l, catalog)),
    },
    {
      kind: 'group',
      label: 'Transitions',
      children: transitions.map((l) => enrichLeaf(l, catalog)),
    },
    {
      kind: 'group',
      label: "Poteaux d'angle",
      children: cornerPosts.map((l) => enrichLeaf(l, catalog)),
    },
    { kind: 'group', label: 'Visserie', children: visserie.map((l) => enrichLeaf(l, catalog)) },
  ];

  // Filter out empty groups to avoid blank sections in the BOM display
  const groups = allGroups.filter((g) => g.children.length > 0);

  return groups;
}

function prettyHardwareLabel(geo: string): string {
  switch (geo) {
    case 'rivet-dome-3-16':
      return 'Rivet alu 3/16" dome';
    case 'rivet-dome-1-4':
      return 'Rivet alu 1/4" dome';
    case 'rivet-countersunk-3-16':
      return 'Rivet alu 3/16" fraisé';
    case 'rivet-countersunk-1-4':
      return 'Rivet alu 1/4" fraisé';
    case 'bolt-hex-1-4-20':
      return 'Boulon hex 1/4"-20';
    case 'bolt-hex-5-16-18':
      return 'Boulon hex 5/16"-18';
    case 'baluster-mtr065075031':
      return 'Barrotin tube rond 3/4"';
    default:
      return geo;
  }
}
